import { ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED } from "@/constants/constants";
import { Drawer } from "@JohnLewisPartnership/jl-design-system/dist/components/drawer/Drawer";
import styles from "./ProtectPlusComplianceDrawer.module.scss";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { TickOutlined as Tick } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickOutlined";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";

import {
  BasketItem,
  getBasket,
  ServiceItem,
} from "@/utils/analytics/getBasket.tsx";
import { VariantServices } from "@/types/types.ts";
import { mappedServices } from "@/utils/services/serviceMapper.ts";
import { useSharedServices } from "@/utils/services/useSharedServices";

import { TransformedExtendedWarrantyMulti } from "@/types/serviceTypes.ts";
import {
  BasketMode,
  OPEN_POST_ADD_TO_BASKET_DRAWER,
  PRE_ADD_TO_BASKET_DRAWER,
} from "./components/AvailableServices";

import { toggleSelectedService } from "@/utils/services/toggleSelectedService.ts";
import { ProtectPlusScheme } from "@/components/PostAddToBasketDrawer/scheme/schemeDecisioner.ts";
import { ProtectPlusCompliance } from "@/components/PostAddToBasketDrawer/components/ProtectPlusCompliance/ProtectPlusCompliance.tsx";
import {
  addServiceToBasket,
  changeItemQuantity,
} from "@/components/PostAddToBasketDrawer/data/basketServicesApi.ts";
import classNames from "classnames";

type Props = {
  title: string;
  variantServices: VariantServices;
  basketBaseUrl: string;
  productId: string;
  skuId: string;
  guarantees?: { title: string }[];
  basketMode: BasketMode;
  protectPlusScheme: ProtectPlusScheme;
  hideFooter: boolean;
  mandatoryServicesOverride: string[];
};

type CachedBasketProduct = {
  quantity: number;
  itemId: string;
  services: Array<ServiceItem>;
};

export const postAddToBasketDrawerEvent = "POST_ADD_TO_BASKET_DRAWER_OPENED";

const findCachedBasketProduct: (
  skuId: string,
) => CachedBasketProduct | undefined = (skuId: string) => {
  const product: BasketItem | undefined = getBasket().product.find(
    (i) => i.sku === skuId,
  );

  if (!product) {
    return;
  }

  return {
    quantity: parseInt(product.qty, 10),
    itemId: product.itemId,
    services: product.services || [],
  };
};

const UPDATE_CHILD_ITEMS = "ADD_TO_BASKET_ESI:CHILD_ITEMS_UPDATED:POST_A2B";
export const ProtectPlusComplianceDrawerUI = ({
  title,
  variantServices,
  skuId,
  basketMode,
  guarantees = [],
  protectPlusScheme,
  basketBaseUrl,
  hideFooter,
  mandatoryServicesOverride,
}: Props) => {
  const drawerHeaderDefault = "Protect Plus";
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [services, updateServices] = useSharedServices([]);
  const commerceItems = useRef<null | Array<any>>(null);
  const isBasketFull = useRef<boolean>(false);
  const [complianceDrawerHeader, setComplianceDrawerHeader] =
    useState<string>(drawerHeaderDefault);
  const [basketQuantity, setBasketQuantity] = useState(0);
  const [apiError, setApiError] = useState(false);
  const showTitleWithoutTick = hideFooter || basketQuantity === 0;
  const extendedWarrantySkuIds = useMemo(
    () =>
      variantServices
        ?.filter((service) => service.__typename === "ExtendedWarranty")
        .map((service) => service.aliases.skuId) ?? [],
    [],
  );

  const openPostAddToBasketDrawer = useCallback(
    (event: CustomEvent<{ errors: [] }>) => {
      const data = event.detail;
      const hasError = data.errors?.length > 0;

      const cachedBasketProduct = findCachedBasketProduct(skuId);

      const protectPlusService = cachedBasketProduct?.services.find((service) =>
        extendedWarrantySkuIds.includes(service.skuId),
      );

      if (protectPlusService) {
        toggleSelectedService(protectPlusService.skuId, services);
        setServiceSelected(protectPlusService.skuId, true);
      }

      if (
        !hasError &&
        cachedBasketProduct &&
        basketMode !== BasketMode.Pre &&
        cachedBasketProduct.quantity > (protectPlusService?.quantity ?? 0)
      ) {
        setIsDrawerOpen(true);
        dispatchMonetateQEvent(postAddToBasketDrawerEvent);
      }
    },
    [skuId],
  );

  const openDrawer = useCallback(() => {
    const { product, subTotal } = getBasket();
    if (product.length == 0 && subTotal != "0.00") {
      isBasketFull.current = true;
    } else {
      isBasketFull.current = false;
    }
    setIsDrawerOpen(true);
    dispatchMonetateQEvent(postAddToBasketDrawerEvent);
  }, [skuId]);

  const openPreAddToBasketDrawer = () => {
    setIsDrawerOpen(true);
  };

  useEffect(() => {
    const fetchAndUpdateServices = async () => {
      const mapServiceResult = mappedServices(variantServices, {
        mandatoryServicesOverride,
      });

      const transformedServices = mapServiceResult.map((service) => {
        const cachedBasketProduct = findCachedBasketProduct(skuId);

        const cachedServices = cachedBasketProduct
          ? cachedBasketProduct.services
          : [];

        const serviceToSelect = cachedServices.find(
          (cachedService) => cachedService.skuId === service.skuId,
        );
        return serviceToSelect
          ? { ...service, selected: true, quantity: serviceToSelect.quantity }
          : { ...service, quantity: 0 };
      });

      let finalServices = transformedServices || services;

      if (
        warrantyService?.selected &&
        basketMode === BasketMode.Pre &&
        extendedWarrantySkuIds.length > 0 &&
        mandatoryServicesOverride !== undefined
      ) {
        finalServices = finalServices.map((service) =>
          extendedWarrantySkuIds.includes(service.skuId)
            ? { ...service, selected: !service.selected }
            : service,
        );

        const extendedWarrantyService = finalServices.find(
          (service) => service.serviceType === "ExtendedWarranty",
        );

        if (
          extendedWarrantyService &&
          extendedWarrantyService.serviceType === "ExtendedWarranty"
        ) {
          toggleSelectedService(
            extendedWarrantyService.skuId,
            finalServices,
            true,
          );
          setServiceSelected(extendedWarrantyService.skuId, true);
        }
      }
      updateServices(finalServices);
    };
    fetchAndUpdateServices();
  }, [
    variantServices,
    basketMode,
    extendedWarrantySkuIds,
    mandatoryServicesOverride,
  ]);

  useEffect(() => {
    if (basketMode == BasketMode.Pre) {
      window.addEventListener(
        PRE_ADD_TO_BASKET_DRAWER,
        openPreAddToBasketDrawer,
      );
    } else {
      window.addEventListener(OPEN_POST_ADD_TO_BASKET_DRAWER, openDrawer);
    }

    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      openPostAddToBasketDrawer,
    );

    return () => {
      if (basketMode == BasketMode.Pre) {
        window.removeEventListener(
          PRE_ADD_TO_BASKET_DRAWER,
          openPreAddToBasketDrawer,
        );
      } else {
        window.removeEventListener(OPEN_POST_ADD_TO_BASKET_DRAWER, openDrawer);
      }
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        openPostAddToBasketDrawer,
      );
    };
  }, [openPostAddToBasketDrawer, openDrawer, skuId]);

  useEffect(() => {
    const cachedBasketProduct = findCachedBasketProduct(skuId);

    if (isDrawerOpen && cachedBasketProduct) {
      const skuIdsToQuantityMap = cachedBasketProduct.services.reduce(
        (acc: Record<string, number>, curr) => {
          acc[curr.skuId] = curr.quantity;
          return acc;
        },
        {},
      );

      updateServices((prevServices) => {
        return prevServices.map((prevService) => ({
          ...prevService,
          quantity: skuIdsToQuantityMap[prevService.skuId] || 0,
        }));
      });
    } else {
      if (commerceItems.current !== null) {
        dispatchEvent(
          new CustomEvent(UPDATE_CHILD_ITEMS, {
            detail: {
              commerceItems: commerceItems.current,
            },
          }),
        );
        commerceItems.current = null;
      }
    }

    if (cachedBasketProduct && cachedBasketProduct.quantity > 0) {
      setComplianceDrawerHeader("Added to your basket");
      setBasketQuantity(cachedBasketProduct.quantity);
    } else {
      setComplianceDrawerHeader(drawerHeaderDefault);
      setBasketQuantity(0);
    }
  }, [isDrawerOpen]);

  function setServiceSelected(serviceId: string, selected = true) {
    updateServices((prevState) =>
      prevState.map((s) => {
        if (serviceId === s.id) {
          return { ...s, selected, quantity: selected ? 1 : 0 };
        }
        return { ...s };
      }),
    );
  }

  function setServiceQuantity(serviceId: string, quantity: number) {
    updateServices((prevState) =>
      prevState.map((s) => {
        if (serviceId === s.id) {
          return { ...s, selected: quantity > 0, quantity: quantity };
        }
        return { ...s };
      }),
    );
  }

  function findServiceInBasketCookie(
    service: TransformedExtendedWarrantyMulti,
  ) {
    const basketServices = findCachedBasketProduct(skuId)?.services || [];
    return basketServices.find((s: ServiceItem) => s.skuId === service.skuId);
  }

  const handleChangeQuantity = async (
    service: TransformedExtendedWarrantyMulti,
    quantity: number,
  ) => {
    const serviceToUpdate = findServiceInBasketCookie(service);

    if (!serviceToUpdate) {
      return false;
    }

    setApiError(false);

    const response = await changeItemQuantity(
      basketBaseUrl,
      serviceToUpdate.itemId,
      quantity,
    );

    if (response.success) {
      commerceItems.current = response.commerceItems;
      setServiceQuantity(service.id, quantity);
    } else {
      setApiError(true);
    }

    return response.success;
  };

  const handleToggleSelectedServiceClick = async (
    service: TransformedExtendedWarrantyMulti,
  ) => {
    toggleSelectedService(service.id, services);
    setServiceSelected(service.id, true);
  };

  const handleAddProtectPlusClick = async (
    service: TransformedExtendedWarrantyMulti,
    quantity: number,
  ) => {
    const cachedBasketProduct = findCachedBasketProduct(skuId);
    if (!cachedBasketProduct) {
      return;
    }

    setApiError(false);

    const result = await addServiceToBasket(
      basketBaseUrl,
      cachedBasketProduct.itemId,
      service.id,
      quantity,
    );

    if (result.success) {
      commerceItems.current = result.commerceItems;
      setServiceQuantity(service.id, quantity);
      window.dispatchEvent(
        new CustomEvent("jl-events.browser.basketQuantityUpdated"),
      );
    } else {
      setApiError(true);
    }
  };

  const warrantyService = services.find(
    (service) => service.serviceType === "ExtendedWarranty",
  );

  if (!warrantyService || !("quantity" in warrantyService)) return null;

  return (
    <Drawer
      isOpen={isDrawerOpen}
      onClose={() => {
        setIsDrawerOpen(false);
      }}
      heading={complianceDrawerHeader}
      className={styles.drawer}
      primaryButtonText="Continue shopping"
      onPrimaryButtonClick={() => {
        setIsDrawerOpen(false);
      }}
      underlinedButtonText={""}
    >
      <div
        className={classNames(
          styles.drawer__titleItem,
          showTitleWithoutTick ? styles.drawer__titleItem_no_tick : "",
        )}
        data-testid={"post-a2b-drawer-product-title"}
      >
        {!showTitleWithoutTick && <Tick size="medium" />}
        <div>
          {basketQuantity > 0 && (
            <>
              <span> {basketQuantity} </span>
              <span className={styles.timesSymbol} aria-label="times">
                X
              </span>
            </>
          )}
          <span> {title} </span>
        </div>
      </div>
      {apiError && (
        <Message
          body="Sorry, something went wrong. Please try again later"
          type="error"
          className={styles.drawer__message}
        />
      )}
      {isBasketFull.current && (
        <Message
          body="You have too many items in your basket. We are unable to add Protect Plus currently"
          type="information"
          className={styles.drawer__message}
        />
      )}
      <ProtectPlusCompliance
        mode={basketMode}
        protectPlusScheme={protectPlusScheme}
        guarantees={guarantees}
        warrantyService={warrantyService}
        addProtectPlusClicked={async (service, serviceQuantity) => {
          if (service.quantity === serviceQuantity) {
            return true;
          }

          if (basketQuantity > 0) {
            await handleAddProtectPlusClick(service, serviceQuantity);
          } else {
            await handleToggleSelectedServiceClick(service);
          }

          return !apiError;
        }}
        closeDrawer={() => {
          setIsDrawerOpen(false);
        }}
        maxAllowedServices={basketQuantity}
        handleStepperChange={handleChangeQuantity}
        hideFooter={hideFooter || isBasketFull.current}
        isDrawerOpen={isDrawerOpen}
      />
    </Drawer>
  );
};
